// Top-right avatar with dropdown — email, Settings link, Logout.
//
// Built without Radix to avoid pulling another dependency: a tiny
// click-outside handler closes the menu, Esc collapses it, and
// arrow keys move focus between items.

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Settings, LogOut, ChevronDown, User, Languages } from 'lucide-react';
import { useWhoami } from '../api/client';
import { cn } from '../lib/cn';
import { STORAGE_KEY as LOCALE_STORAGE_KEY, SUPPORTED_LOCALES, type Locale } from '../i18n';

function initials(emailOrName: string): string {
  if (!emailOrName) return '?';
  const stripped = emailOrName.split('@')[0] ?? '';
  const parts = stripped.split(/[._\-\s]+/).filter(Boolean);
  if (parts.length === 0) return stripped.slice(0, 2).toUpperCase();
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');
}

const LOCALE_LABEL_KEYS: Record<Locale, string> = {
  en: 'userMenu.languageEnglish',
  de: 'userMenu.languageGerman',
};

export function UserMenu() {
  const { user, loading } = useWhoami();
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const changeLocale = (locale: Locale) => {
    i18n.changeLanguage(locale);
    globalThis.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (loading) {
    return <span className="text-sm text-fg-muted">…</span>;
  }
  if (!user) {
    return (
      <Link to="/login" className="text-sm text-accent hover:underline">
        {t('userMenu.signIn')}
      </Link>
    );
  }

  const display = user.displayName || user.email;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-2 h-9 pl-1 pr-2 rounded-md border transition-colors',
          open
            ? 'border-accent bg-bg-elevated'
            : 'border-border hover:border-border-strong hover:bg-bg-hover',
        )}
      >
        <span className="h-7 w-7 rounded-full bg-accent/15 border border-accent/30 text-accent text-xs font-semibold flex items-center justify-center">
          {initials(display)}
        </span>
        <span className="text-sm text-fg max-w-[160px] truncate">{display}</span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 text-fg-muted transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          className={cn(
            'absolute right-0 top-full mt-2 w-72 z-50',
            'rounded-md border border-border bg-bg-elevated shadow-xl overflow-hidden',
          )}
          role="menu"
        >
          <div className="px-4 py-3 border-b border-border">
            <p className="orb-eyebrow text-fg-subtle">{t('userMenu.signedInAs')}</p>
            <p className="text-sm text-fg font-medium truncate mt-1">
              {user.displayName ?? user.email}
            </p>
            {user.displayName && (
              <p className="text-xs text-fg-muted truncate">{user.email}</p>
            )}
            {user.roles && user.roles.length > 0 && (
              <p className="text-[10px] text-fg-subtle mt-1.5 font-mono">
                {user.roles.join(' · ')}
              </p>
            )}
          </div>
          <div className="py-1">
            <Link
              to="/settings"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-fg hover:bg-bg-hover/60"
            >
              <Settings className="h-4 w-4 text-fg-muted" />
              <span>{t('userMenu.settings')}</span>
            </Link>
            <Link
              to="/tokens"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-fg hover:bg-bg-hover/60"
            >
              <User className="h-4 w-4 text-fg-muted" />
              <span>{t('userMenu.apiTokens')}</span>
            </Link>
          </div>
          <div className="py-2 border-t border-border">
            <p className="flex items-center gap-2 px-4 pb-1.5 orb-eyebrow text-fg-subtle">
              <Languages className="h-3.5 w-3.5" />
              <span>{t('userMenu.language')}</span>
            </p>
            <fieldset className="px-4 flex gap-1.5 border-0 p-0 m-0 min-w-0" aria-label={t('userMenu.language')}>
              {SUPPORTED_LOCALES.map((locale) => (
                <button
                  key={locale}
                  type="button"
                  aria-pressed={i18n.resolvedLanguage === locale}
                  onClick={() => changeLocale(locale)}
                  className={cn(
                    'rounded-md border px-2.5 py-1 text-xs transition-colors',
                    i18n.resolvedLanguage === locale
                      ? 'border-accent bg-accent/15 text-accent'
                      : 'border-border text-fg-muted hover:border-border-strong hover:bg-bg-hover',
                  )}
                >
                  {t(LOCALE_LABEL_KEYS[locale])}
                </button>
              ))}
            </fieldset>
          </div>
          <div className="py-1 border-t border-border">
            <form method="post" action="/auth/logout">
              <button
                type="submit"
                role="menuitem"
                className="flex w-full items-center gap-3 px-4 py-2 text-sm text-fg-muted hover:text-danger hover:bg-danger/10"
              >
                <LogOut className="h-4 w-4" />
                <span>{t('userMenu.logout')}</span>
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
